import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Download } from '@mui/icons-material';
import { superAdminService } from '../../../services/superAdmin/superAdminService';
import { exportToExcel } from '../../../utils/excelExport';

const UsageStatsPage = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['superAdminUsageStats'],
    queryFn: async () => {
      const result = await superAdminService.getUsageStats();
      if (!result.success) throw new Error(result.message);
      return result.data;
    }
  });

  const handleExport = useCallback(() => {
    if (!data) return;
    exportToExcel({
      title: 'Usage Statistics',
      fileName: 'EasyCheck_UsageStats.xlsx',
      sheetName: 'Usage Stats',
      columns: [
        { header: 'Company', key: 'companyName', type: 'text', width: 30 },
        { header: 'Active', key: 'isActive', type: 'text' },
        { header: 'Users', key: 'userCount', type: 'number' },
        { header: 'Cheques Printed', key: 'chequesPrinted', type: 'number' },
        { header: 'Banks', key: 'bankCount', type: 'number' },
        { header: 'Beneficiaries', key: 'beneficiaryCount', type: 'number' },
        { header: 'Templates', key: 'templateCount', type: 'number' },
        { header: 'Last Active', key: 'lastActiveDate', type: 'date' },
      ],
      data: data.map(c => ({
        ...c,
        isActive: c.isActive ? 'Yes' : 'No',
        lastActiveDate: c.lastActiveDate ? new Date(c.lastActiveDate).toLocaleDateString('en-GB') : 'Never',
      })),
    });
  }, [data]);

  const columns = [
    { field: 'companyName', headerName: 'Company', flex: 1, minWidth: 200 },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 90,
      renderCell: ({ value }) => (
        <Chip
          label={value ? 'Active' : 'Inactive'}
          color={value ? 'success' : 'default'}
          size="small"
          sx={{ fontSize: '0.7rem' }}
        />
      )
    },
    { field: 'userCount', headerName: 'Users', width: 80, type: 'number' },
    { field: 'chequesPrinted', headerName: 'Cheques', width: 90, type: 'number' },
    { field: 'bankCount', headerName: 'Banks', width: 80, type: 'number' },
    { field: 'beneficiaryCount', headerName: 'Beneficiaries', width: 110, type: 'number' },
    { field: 'templateCount', headerName: 'Templates', width: 100, type: 'number' },
    {
      field: 'lastActiveDate',
      headerName: 'Last Active',
      width: 130,
      valueFormatter: (params) => {
        const val = params?.value ?? params;
        return val ? new Date(val).toLocaleDateString() : 'Never';
      }
    }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Usage Stats</Typography>
        <Button
          variant="outlined"
          startIcon={<Download />}
          onClick={handleExport}
          size="small"
          sx={{ borderColor: '#14b8a6', color: '#14b8a6', '&:hover': { borderColor: '#0d9488', backgroundColor: '#f0fdfa' } }}
        >
          Export Excel
        </Button>
      </Box>

      <Box sx={{ height: 'calc(100vh - 200px)', backgroundColor: 'white', borderRadius: 3, border: '1px solid #e2e8f0' }}>
        <DataGrid
          rows={data || []}
          columns={columns}
          loading={isLoading}
          disableRowSelectionOnClick
          initialState={{
            sorting: { sortModel: [{ field: 'chequesPrinted', sort: 'desc' }] }
          }}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f8fafc' }
          }}
        />
      </Box>
    </Box>
  );
};

export default UsageStatsPage;
